import { useCallback, useEffect, useMemo, useState } from 'react';
import { goBack, navigate, resetNav } from '../../app/navigation';
import { clientNameOf, displayAddress, isInspectionLocked } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { basementFinishLabel, basementTypeLabel, propertyTypeLabel } from '../../i18n/labels';
import { jsPdfRenderer } from '../../report/pdf';
import { shareAgreementPdfAction } from '../../state/agreementPdf';
import { useStore } from '../../state/createStore';
import { findingStore } from '../../state/findingStore';
import { formalEligibilityBlockedFor, formalWorkflowBlockedFor } from '../../state/formalWorkflow';
import { completeInspectionAction, inspectionStore, reopenInspectionAction, setPropertyFactsAction, updateAgreementAction, updateInspectionDetailsAction, withdrawAiConsentAction } from '../../state/inspectionStore';
import { buildInspectionOverview } from '../../state/reportOverview';
import { Banner, Button, Card, EmptyState, Field, InlineMessage, SectionHeader, TextArea, TextInput, TopBar, showToast } from '../../ui/components';
import { formatClock, formatDate, useAutosaveText } from '../../ui/hooks';
import { AgreementCard } from './AgreementCard';
import { PropertyFactRows } from './PropertyFactRows';

// Inspection details — the report-header side of an inspection (property / client / inspector /
// date-time / weather), the Inspection Agreement (fee or Demo, signatures, the fixed document, its
// own PDF), the inspection-level notes, and the explicit completion state. Read-only once the
// report is generated. Unknown property facts print as 未设置, never as a default (Round 2 §D).
function toLocalInputValue(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function InspectionDetailsScreen(props: { inspectionId: string; focus?: 'agreement' }) {
  const { t } = useT();
  // 「去签协议」 from an AI card (Owner 2026-10-07): land on the Inspection Agreement, not the top.
  const focusAgreement = props.focus === 'agreement';
  useEffect(() => {
    if (!focusAgreement) return;
    // Android acceptance 2026-10-21 (issue 5): the shell's per-page scroll reset (src/app/scrollRestore.ts)
    // is a PARENT effect and runs after this one, so an immediate scroll was undone and the agreement sat
    // at the bottom, half under the tab bar. Scroll once the reset has happened.
    // The inspector came here to sign: bring 签署协议 itself on screen (the agreement block is taller than
    // the screen, so its top alone left the button under the tab bar).
    const timer = window.setTimeout(() => {
      const target = document.querySelector<HTMLElement>('[data-testid="details-agreement"] [data-testid="agreement-sign"]') ?? document.querySelector<HTMLElement>('[data-testid="details-agreement"]');
      // jsdom has no scrollIntoView; a WebView always does.
      if (target && typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [focusAgreement]);
  const inspectionState = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const overview = useMemo(() => {
    const inspection = inspectionState.inspections.find((i) => i.id === props.inspectionId);
    return inspection ? buildInspectionOverview(inspection, inspectionState.areas, inspectionState.items, findingState.findings) : null;
  }, [inspectionState, findingState, props.inspectionId]);
  const [message, setMessage] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  const saveNotes = useCallback((value: string) => updateInspectionDetailsAction(props.inspectionId, { notes: value }), [props.inspectionId]);
  const notes = useAutosaveText(overview?.inspection.notes ?? '', saveNotes);
  const saveWeather = useCallback((value: string) => updateInspectionDetailsAction(props.inspectionId, { weather: value }), [props.inspectionId]);
  const weather = useAutosaveText(overview?.inspection.weather ?? '', saveWeather);

  if (!overview) {
    return (
      <div className="screen">
        <TopBar title={t('details.title')} onBack={() => resetNav({ screen: 'inspectionList' })} />
        <EmptyState title={t('inspection.notFoundTitle')} body={t('inspection.notFoundBody')} />
      </div>
    );
  }

  const inspection = overview.inspection;
  const locked = isInspectionLocked(inspection);
  // The client's AI consent as it stands: given on the current document and not withdrawn.
  const consentActive = inspection.agreement.clientAiConsent && inspection.agreement.aiConsentWithdrawnAt === null;
  const canComplete = overview.completion.enabledAreas > 0 && overview.completion.completedAreas === overview.completion.enabledAreas;
  // NY F1 Round 1 (§15.1 effects 2 and 3). The record stays fully readable and the agreement stays
  // CORRECTABLE — only executing it and completing the inspection are withheld.
  // Round 5 (X-1): SIGNING asks eligibility only — an unsigned agreement is exactly what this card
  // exists to resolve, so the full gate (which includes "not signed yet") must not hide the button.
  const gateBlocked = formalWorkflowBlockedFor(inspection);
  const signingBlocked = formalEligibilityBlockedFor(inspection);
  const notSet = t('common.notSet');

  async function handleComplete() {
    setMessage(null);
    const outcome = await completeInspectionAction(props.inspectionId);
    if (!outcome.ok) setMessage(t('details.completeBlocked'));
  }

  async function sharePdf() {
    setSharing(true);
    setMessage(null);
    try {
      const outcome = await shareAgreementPdfAction(props.inspectionId, jsPdfRenderer);
      if (outcome.ok) showToast(t('agreement.pdfShared'));
      else {
        if (outcome.reason !== 'unsigned') console.warn('[agreement] pdf failed:', outcome.reason, outcome.detail);
        setMessage(outcome.reason === 'unsigned' ? t('agreement.pdfUnsigned') : outcome.reason === 'shareFailed' ? t('report.shareFailed', { detail: outcome.detail }) : t('agreement.pdfFailed'));
      }
    } finally {
      setSharing(false);
    }
  }

  const rows: Array<[string, string]> = [
    [t('details.address'), inspection.property.address || notSet],
    [t('details.cityStateZip'), [inspection.property.city, inspection.property.state, inspection.property.zip].filter((s) => s.length > 0).join(' ') || notSet],
    [t('details.typeYear'), `${propertyTypeLabel(t, inspection.property.propertyType)}${inspection.property.yearBuilt ? ` · ${inspection.property.yearBuilt}` : ''}`],
    [
      t('layout.basementDetails'),
      inspection.property.basementType ? `${basementTypeLabel(t, inspection.property.basementType)}${inspection.property.basementFinish ? ` · ${basementFinishLabel(t, inspection.property.basementFinish)}` : ` · ${t('layout.finishNotSet')}`}` : notSet
    ],
    [t('details.client'), `${clientNameOf(inspection) || notSet}${inspection.client.phone ? ` · ${inspection.client.phone}` : ''}${inspection.client.email ? ` · ${inspection.client.email}` : ''}`],
    // The licence AS IT WAS when this inspection was created — the record carries its own snapshot
    // (it is what the report prints), so a licence changed in Settings later never rewrites history.
    [
      t('details.inspector'),
      `${inspection.inspector.name || notSet}${
        inspection.inspector.licenseNumber.trim().length > 0 ? ` · ${t('home.license', { state: inspection.inspector.licensedState ?? '', number: inspection.inspector.licenseNumber.trim() })}` : ''
      }`
    ],
    [t('details.created'), formatDate(inspection.createdAt)]
  ];

  return (
    <div className="screen" data-testid="inspection-details">
      <TopBar title={t('details.title')} subtitle={displayAddress(inspection, t('inspection.noAddress'))} onBack={goBack} />
      <div className="screen-body">
        {locked ? (
          <div className="banner-slot">
            <Banner tone="info" icon="lock" title={t('report.lockedTitle')} body={t('report.lockedBody')} testId="details-locked" action={<Button variant="text" size="small" block={false} onClick={() => navigate({ screen: 'report', inspectionId: props.inspectionId })}>{t('nav.reports')}</Button>} />
          </div>
        ) : null}
        <Card testId="overview-header">
          {rows.map(([key, value]) => (
            <div className="kv" key={key}>
              <span className="kv-key">{key}</span>
              <span className="kv-val">{value}</span>
            </div>
          ))}
          <div className="details-weather form-stack">
            <Field label={t('setup.inspectionAt')} htmlFor="details-inspection-at">
              <div className="field-datetime">
                <input
                  id="details-inspection-at"
                  className="field-input"
                  type="datetime-local"
                  value={toLocalInputValue(inspection.inspectionAt)}
                  disabled={locked}
                  onChange={(event) => {
                    const parsed = Date.parse(event.target.value);
                    if (Number.isFinite(parsed)) void updateInspectionDetailsAction(props.inspectionId, { inspectionAt: parsed });
                  }}
                  data-testid="details-inspection-at"
                />
              </div>
            </Field>
            <Field label={t('details.weather')} htmlFor="ov-weather">
              <TextInput id="ov-weather" value={weather.value} onChange={weather.setValue} placeholder={t('setup.weatherPlaceholder')} disabled={locked} testId="details-weather" />
            </Field>
          </div>
        </Card>

        {message ? <InlineMessage kind="err" text={message} /> : null}
        <Card testId="details-facts">
          <PropertyFactRows
            heatingFuel={inspection.property.heatingFuel}
            waterSupplySource={inspection.property.waterSupplySource}
            wasteDisposalSource={inspection.property.wasteDisposalSource}
            disabled={locked}
            onChange={(patch) => void setPropertyFactsAction(props.inspectionId, patch)}
            testId="details-facts"
          />
        </Card>
        <AgreementCard
          agreement={inspection.agreement}
          subject={inspection}
          readOnly={locked}
          signingBlocked={signingBlocked}
          onChange={(next) => void updateAgreementAction(props.inspectionId, next)}
          onSharePdf={() => void sharePdf()}
          sharing={sharing}
          remote={{ inspection }}
          testId="details-agreement"
        />

        {/* OD-8 (agreement v2 §1): where the client's AI decision stands, and the written withdrawal
            they may give before the report is generated. Withdrawing closes the AI path only — the
            signed agreement and the field work stand. */}
        <Card testId="details-consent">
          <SectionHeader title={t('agreement.aiConsentTitle')} />
          <div className="reason-summary">
            <span className={`badge ${consentActive ? 'badge-ok' : 'badge-muted'}`} data-testid="details-consent-state">
              {consentActive ? t('agreement.aiConsentGiven') : t('agreement.aiConsentNone')}
            </span>
            {inspection.agreement.aiConsentWithdrawnAt !== null ? (
              <span className="card-note" data-testid="details-consent-withdrawn">
                {t('agreement.consentWithdrawnOn', { date: formatDate(inspection.agreement.aiConsentWithdrawnAt) })}
              </span>
            ) : null}
            {consentActive && !locked ? (
              <Button variant="text" size="small" block={false} onClick={() => void withdrawAiConsentAction(props.inspectionId)} testId="details-consent-withdraw">
                {t('agreement.aiConsentWithdraw')}
              </Button>
            ) : null}
          </div>
        </Card>

        <Card testId="overview-notes">
          <SectionHeader title={t('details.notes')} />
          <TextArea value={notes.value} onChange={notes.setValue} placeholder={t('details.notesPlaceholder')} disabled={locked} testId="details-notes" />
          <p className="saved-note">{notes.savedAt ? t('common.autosaved', { time: formatClock(notes.savedAt) }) : t('common.autosaveHint')}</p>
        </Card>

        <Card testId="overview-status">
          <SectionHeader title={t('details.status')} />
          {locked ? (
            <p className="card-note">{t('report.lockedBody')}</p>
          ) : inspection.status === 'completed' ? (
            <Button variant="secondary" onClick={() => void reopenInspectionAction(props.inspectionId)} testId="reopen-inspection">
              {t('details.reopen')}
            </Button>
          ) : (
            <>
              {gateBlocked ? <p className="blockers-note">{t('gate.actionUnavailable')}</p> : !canComplete ? <p className="blockers-note">{t('details.completeBlocked')}</p> : null}
              <Button onClick={() => void handleComplete()} disabled={!canComplete || gateBlocked} testId="complete-inspection">
                {t('details.markComplete')}
              </Button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
